<script setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { resolveColourValue } from '@/Helpers/themeColourResolver';

const page = usePage();

const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    brand: { type: Object, default: () => ({}) },
    shift: { type: Number, default: 0 },
    scormExport: Boolean,
});

const avatarBgHex = computed(() => resolveColourValue(props.row?.content?.avatarBgColour, props.brand, props.shift));
const avatarTextHex = computed(() => resolveColourValue(props.row?.content?.avatarTextColour, props.brand, props.shift));
const nameHex = computed(() => resolveColourValue(props.row?.content?.nameColour, props.brand, props.shift));
const titleHex = computed(() => resolveColourValue(props.row?.content?.titleColour, props.brand, props.shift));
const testimonialHex = computed(() => resolveColourValue(props.row?.content?.testimonialColour, props.brand, props.shift));

const avatarStyle = computed(() => ({
    width: `${props.row?.content?.avatarSize ?? 52}px`,
    height: `${props.row?.content?.avatarSize ?? 52}px`,
    borderRadius: `${props.row?.content?.avatarRounding ?? 50}%`,
}));

const avatarInitialsStyle = computed(() => ({
    ...avatarStyle.value,
    backgroundColor: avatarBgHex.value,
    color: avatarTextHex.value,
}));

function textStyle(colour, font, bold, italic, defaultBold = false, defaultItalic = false) {
    const style = { color: colour };
    if (font) style.fontFamily = font.replace(/_/g, ' ');
    const isBold = bold === undefined ? defaultBold : bold;
    const isItalic = italic === undefined ? defaultItalic : italic;
    style.fontWeight = isBold ? '700' : '400';
    style.fontStyle = isItalic ? 'italic' : 'normal';
    return style;
}

const nameSizeClass = computed(() => props.row?.content?.nameFontSize || 'text-fl-base');
const nameStyle = computed(() => textStyle(
    nameHex.value,
    props.row?.content?.nameFont,
    props.row?.content?.nameBold,
    props.row?.content?.nameItalic,
    true,
    false,
));

const titleSizeClass = computed(() => props.row?.content?.titleFontSize || 'text-fl-sm');
const titleStyle = computed(() => textStyle(
    titleHex.value,
    props.row?.content?.titleFont,
    props.row?.content?.titleBold,
    props.row?.content?.titleItalic,
    false,
    false,
));

const testimonialSizeClass = computed(() => props.row?.content?.testimonialFontSize || 'text-fl-lg');
const testimonialStyle = computed(() => textStyle(
    testimonialHex.value,
    props.row?.content?.testimonialFont,
    props.row?.content?.testimonialBold,
    props.row?.content?.testimonialItalic,
    false,
    true,
));

const initials = computed(() => {
    if (!props.row.content?.name) return '';
    const parts = props.row.content.name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
});

const avatarUrl = computed(() => {
    if (!props.row.content?.avatarFolder || !props.row.content?.avatarFile) return '';
    if (props.scormExport) return props.row.content.avatarFolder + '/' + props.row.content.avatarFile;
    return 'https://' + page.props.s3_bucket + '.s3.' + page.props.s3_region + '.amazonaws.com/' + props.row.content.avatarFolder + '/' + props.row.content.avatarFile;
});
</script>

<template>
    <div v-if="row.content?.name" class="w-full p-fl-4">
        <!-- Avatar + Name -->
        <div class="flex items-center gap-3 mb-4">
            <div
                v-if="!avatarUrl"
                class="flex items-center justify-center shrink-0 font-bold text-fl-sm"
                :style="avatarInitialsStyle"
            >
                {{ initials }}
            </div>
            <img
                v-else
                class="shrink-0 object-cover"
                :src="avatarUrl"
                :style="avatarStyle"
            >
            <div class="flex flex-col min-w-0">
                <span
                    class="leading-tight break-words"
                    :class="nameSizeClass"
                    :style="nameStyle"
                    v-text="row.content.name"
                />
                <span
                    v-if="row.content.title"
                    class="leading-tight break-words"
                    :class="titleSizeClass"
                    :style="titleStyle"
                    v-text="row.content.title"
                />
            </div>
        </div>

        <!-- Testimonial -->
        <p
            class="leading-snug whitespace-pre-line break-words"
            :class="testimonialSizeClass"
            :style="testimonialStyle"
            v-text="row.content.testimonial"
        />
    </div>
</template>
